import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, X } from 'lucide-react';

const ContactModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phone: '',
    employeeCount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://report.hirethink.ai/api/Email/SendContactForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        setTimeout(() => {
          onClose();
          setIsSuccess(false);
          setFormData({ fullname: '', email: '', phone: '', employeeCount: '' });
        }, 3000);
      } else {
        throw new Error('Form gönderilemedi');
      }
    } catch (error) {
      // console.error('Form gönderme hatası:', error);
      alert('Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative overflow-hidden my-8">
        {/* Header */}
        <div className="bg-primary p-4 sm:p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors duration-200 p-2 touch-manipulation"
            aria-label="Kapat"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <h2 className="text-xl sm:text-2xl font-heading text-center pr-10">{t('contact.title')}</h2>
          <p className="text-white/80 text-center mt-2 text-sm sm:text-base">{t('contact.subtitle')}</p>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6">
          {isSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-accent-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-accent-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contact.successTitle')}</h3>
              <p className="text-gray-600">{t('contact.successMessage')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="fullname" className="block text-xs sm:text-sm font-label text-gray-700">
                  {t('contact.fullName')}
                </label>
                <input
                  type="text"
                  id="fullname"
                  name="fullname"
                  placeholder={t('contact.fullNamePlaceholder')}
                  value={formData.fullname}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 touch-manipulation"
                />
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="email" className="block text-xs sm:text-sm font-label text-gray-700">
                  {t('contact.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('contact.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 touch-manipulation"
                />
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="phone" className="block text-xs sm:text-sm font-label text-gray-700">
                  {t('contact.phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={t('contact.phonePlaceholder')}
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 touch-manipulation"
                />
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="employeeCount" className="block text-xs sm:text-sm font-label text-gray-700">
                  {t('contact.employeeCount')}
                </label>
                <select
                  id="employeeCount"
                  name="employeeCount"
                  value={formData.employeeCount}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 touch-manipulation"
                >
                  <option value="">{t('contact.employeeCountPlaceholder')}</option>
                  <option value="0-10">0-10</option>
                  <option value="11-50">11-50</option>
                  <option value="50-500">50-500</option>
                  <option value="500+">500+</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent-orange hover:bg-accent-orange-hover text-white py-3 px-6 rounded-full font-button transition-all duration-300 transform hover:scale-105 hover:shadow-orange-glow disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('contact.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t('contact.submit')}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
